import { useEffect, useState } from 'react';
import { Trash2, Star, Edit, Save, X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { reviewsAPI } from '../../services/api';
import { toast } from 'sonner';

const EMPTY_REVIEW = {
  name: '',
  rating: 5,
  title: '',
  vehicle: '',
  text: '',
  productImage: '',
  productId: '',
};

const ReviewsAdmin = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newReview, setNewReview] = useState(EMPTY_REVIEW);

  const load = async () => {
    setLoading(true);
    try {
      const data = await reviewsAPI.getAll();
      setReviews(data);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await reviewsAPI.delete(id);
      toast.success('Review deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleFeatured = async (r) => {
    try {
      await reviewsAPI.update(r.id, { featured: !r.featured });
      toast.success(r.featured ? 'Unfeatured' : 'Featured');
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const saveEdit = async () => {
    try {
      const { id, ...payload } = editing;
      await reviewsAPI.update(id, payload);
      toast.success('Review updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleCreate = async () => {
    if (!newReview.name || !newReview.title || !newReview.text) {
      toast.error('Name, title and review text are required');
      return;
    }
    try {
      await reviewsAPI.create(newReview);
      toast.success('Review created');
      setCreating(false);
      setNewReview(EMPTY_REVIEW);
      load();
    } catch (e) {
      toast.error('Failed to create review');
    }
  };

  return (
    <div className="space-y-4" data-testid="reviews-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Reviews</h2>
          <p className="text-sm text-gray-500">{reviews.length} review{reviews.length === 1 ? '' : 's'} total</p>
        </div>
        <Button
          size="lg"
          onClick={() => { setCreating(true); setNewReview(EMPTY_REVIEW); }}
          data-testid="add-review-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        >
          <Plus className="mr-2 h-5 w-5" /> Add Review
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-10">Loading…</p>
      ) : reviews.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No reviews yet.</p>
      ) : (
        <div className="grid gap-3">
          {reviews.map((r) => (
            <Card key={r.id} data-testid={`review-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {r.productImage && (
                    <img src={r.productImage} alt="" className="w-16 h-16 object-contain bg-gray-50 rounded flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{r.name}</span>
                      <span className="text-xs text-gray-500">· {r.date}</span>
                      <div className="flex">
                        {[1,2,3,4,5].map((i) => (
                          <Star key={i} className={`h-3 w-3 ${i <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {r.featured && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Featured</span>}
                    </div>
                    <p className="font-medium text-sm mt-1">{r.title}</p>
                    <p className="text-xs text-gray-500 line-clamp-1">{r.vehicle}</p>
                    <p className="text-sm text-gray-700 mt-2 line-clamp-3">{r.text}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="outline" onClick={() => toggleFeatured(r)} data-testid={`feature-${r.id}`}>
                      <Star className={`h-3 w-3 ${r.featured ? 'fill-amber-400 text-amber-400' : ''}`} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...r })} data-testid={`edit-review-${r.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-review-${r.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <Card className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Edit Review</h3>
                <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Author</label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Rating</label>
                  <Input type="number" min="1" max="5" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Title</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Vehicle</label>
                <Input value={editing.vehicle} onChange={(e) => setEditing({ ...editing, vehicle: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Text</label>
                <textarea
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  rows={5}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Product image URL</label>
                <Input value={editing.productImage || ''} onChange={(e) => setEditing({ ...editing, productImage: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="save-review-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setCreating(false)}>
          <Card className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Add New Review</h3>
                <button onClick={() => setCreating(false)} aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Author *</label>
                  <Input value={newReview.name} onChange={(e) => setNewReview({ ...newReview, name: e.target.value })} placeholder="e.g. John S" data-testid="new-review-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Rating *</label>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, rating: i })}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star className={`h-6 w-6 ${i <= newReview.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Title *</label>
                <Input value={newReview.title} onChange={(e) => setNewReview({ ...newReview, title: e.target.value })} placeholder="e.g. Massive difference!" data-testid="new-review-title" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Vehicle</label>
                <Input value={newReview.vehicle} onChange={(e) => setNewReview({ ...newReview, vehicle: e.target.value })} placeholder="e.g. BMW 320d 2020" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Text *</label>
                <textarea
                  value={newReview.text}
                  onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                  rows={5}
                  className="w-full p-2 border rounded text-sm"
                  data-testid="new-review-text"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Product Image URL (optional)</label>
                <Input value={newReview.productImage} onChange={(e) => setNewReview({ ...newReview, productImage: e.target.value })} placeholder="https://… or /images/…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Linked Product ID (optional)</label>
                <Input value={newReview.productId} onChange={(e) => setNewReview({ ...newReview, productId: e.target.value })} placeholder="e.g. p-6597" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="create-review-submit">
                  <Save className="h-4 w-4 mr-1" /> Create Review
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReviewsAdmin;
